// A minimal protobuf codec for LiveKit's signaling messages, driven by the field tables in signalSchema.js.
// Supports what those messages use: varints (bool, enums, 32/64-bit ints), strings, bytes and nested
// messages. Unknown fields are skipped, so the server can send fields the tables leave out.
// Varints are plain numbers, exact up to 2^53: the only 64-bit values in use are millisecond timestamps.

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const VARINT_TYPES = [ 'bool', 'enum', 'int32', 'uint32', 'int64' ];

class Writer {
	constructor() { this.bytes = []; }

	writeVarint(value) {
		let v = Math.floor(value);
		if(v < 0) throw new Error('protobuf: negative varints are not supported');
		while(v > 0x7f) {
			this.bytes.push((v % 0x80) | 0x80);
			v = Math.floor(v / 0x80);
		}
		this.bytes.push(v);
	}

	writeBytes(bytes) {
		this.writeVarint(bytes.length);
		for(const b of bytes) this.bytes.push(b);
	}

	finish() { return new Uint8Array(this.bytes); }
}

class Reader {
	constructor(bytes) {
		this.bytes = bytes;
		this.pos = 0;
	}

	readVarint() {
		let result = 0;
		let factor = 1;
		for(;;) {
			if(this.pos >= this.bytes.length) throw new Error('protobuf: truncated varint');
			const b = this.bytes[this.pos++];
			result += (b & 0x7f) * factor;
			if(!(b & 0x80)) return result;
			factor *= 0x80;
		}
	}

	readBytes() {
		const length = this.readVarint();
		const bytes = this.bytes.subarray(this.pos, this.pos + length);
		this.pos += length;
		return bytes;
	}

	skip(wireType) {
		if(wireType === 0) this.readVarint();
		else if(wireType === 1) this.pos += 8;
		else if(wireType === 2) this.readBytes();
		else if(wireType === 5) this.pos += 4;
		else throw new Error(`protobuf: unsupported wire type ${wireType}`);
	}
}

function convertVarint(type, value) {
	if(type === 'bool') return value !== 0;
	if(type === 'int32' && value >= 0x80000000) return value - 0x100000000;
	return value;
}

function encodeInto(writer, schema, name, message) {
	const fields = schema[name];
	if(!fields) throw new Error(`protobuf: unknown message ${name}`);
	for(const [ key, [ no, type, repeated ] ] of Object.entries(fields)) {
		const value = message[key];
		if(value === undefined || value === null) continue;
		for(const item of repeated ? value : [ value ]) {
			if(VARINT_TYPES.includes(type)) {
				writer.writeVarint(no << 3);
				writer.writeVarint(type === 'bool' ? (item ? 1 : 0) : item);
			} else {
				writer.writeVarint((no << 3) | 2);
				if(type === 'string') writer.writeBytes(textEncoder.encode(item));
				else if(type === 'bytes') writer.writeBytes(item);
				else writer.writeBytes(encode(schema, type, item));
			}
		}
	}
}

export function encode(schema, name, message) {
	const writer = new Writer();
	encodeInto(writer, schema, name, message);
	return writer.finish();
}

export function decode(schema, name, bytes) {
	const fields = schema[name];
	if(!fields) throw new Error(`protobuf: unknown message ${name}`);
	const byNumber = {};
	for(const [ key, [ no, type, repeated ] ] of Object.entries(fields)) byNumber[no] = { key, type, repeated };

	const message = {};
	for(const { key, repeated } of Object.values(byNumber)) if(repeated) message[key] = [];

	const reader = new Reader(bytes);
	while(reader.pos < bytes.length) {
		const tag = reader.readVarint();
		const no = tag >>> 3;
		const wireType = tag & 7;
		const field = byNumber[no];
		if(!field) {
			reader.skip(wireType);
			continue;
		}
		const { key, type, repeated } = field;
		const store = value => { if(repeated) message[key].push(value); else message[key] = value; };
		if(VARINT_TYPES.includes(type)) {
			if(wireType === 2) {
				// Packed repeated varints.
				const packed = new Reader(reader.readBytes());
				while(packed.pos < packed.bytes.length) store(convertVarint(type, packed.readVarint()));
			} else {
				store(convertVarint(type, reader.readVarint()));
			}
		} else if(type === 'string') {
			store(textDecoder.decode(reader.readBytes()));
		} else if(type === 'bytes') {
			store(reader.readBytes().slice());
		} else {
			store(decode(schema, type, reader.readBytes()));
		}
	}
	return message;
}
